// @ts-check
/**
 * **仮想フィールドの座標系**（Issue #522）。実写の動画が無くても盤を置けるよう、
 * 理想のグリッド（きれいに並んだ盤）を平面の座標に載せる。
 *
 * 単位は**カード 1 枚の幅 = 1**で書き、`unit`（平面のピクセル）を掛けて平面に載せる。
 * P1（team 0）が手前（v が大きい側）で、P2 は原点を中心に 180° 回した位置になる。
 *
 * 実写と違って **EX デッキと手札も引ける**（`overlay_zones` に無いので実写の Field は答えられない）。
 * **どのカメラで見るか（真上 / 斜め）はここの関心ではない** —— 盤の座標は 1 つで、見え方はカメラが決める。
 * エクストラモンスターゾーンはこの動画のルールに無いので持たない。
 */
import { toField, DEFAULT_UNIT } from '../core/field';
import { LIVE_PLANE } from './live';
import { ZONE_SLOTS, zoneKey } from '../core/field/zones';

/** 理想のグリッド（カード 1 枚の幅 = 1） */
export const IDEAL = Object.freeze({
  cardW: 1,
  cardH: 1.46,
  gapX: 1.35,
  rowZ: Object.freeze({ monster: 1.1, magic_trap: 2.75, hand: 4.55 }),
});

/**
 * 理想のグリッドでのゾーンの中心（カード 1 枚の幅が単位。+z が P1 の側）。
 *
 * **並びの番号は両プレイヤーとも「画面の左から」**（ユーザー判断 2026-09-24）。
 * ゾーンのキー `monster:{player}:{pos}` の意味は**実写の Field が決めている**もので、
 * フィールド生成の `playmat_zones.py::_playmat_index` が「React の pos（1..5、画面の左から順）」
 * として並べている（P2 のマットは合成で 180° 回るので、そこだけ番号を逆にして左端を pos 1 にしている）。
 * **同じキーが同じ場所を指さないと、実写と仮想を切り替えたときにカードが動く**ので合わせる。
 *
 * 1 人に 1 つしかない枠（墓地・除外・デッキ・EX・フィールド魔法）は**番号を持たない**ので、
 * 実写のマットどおり 180° 回した位置のまま（P1 の墓地が右なら P2 の墓地は左）。
 *
 * @param {string} kind `zones.ZONE_KINDS` のどれか
 * @param {number} player
 * @param {number} [seq] 0 起算
 */
export function idealPosition(kind, player, seq = 0) {
  const side = player === 1 ? -1 : 1;
  const g = IDEAL.gapX;
  const at = (x, z) => ({ x: x * side, z: z * side });
  if (kind === 'field') return at(-3 * g, IDEAL.rowZ.monster);
  if (kind === 'grave') return at(3 * g, IDEAL.rowZ.monster);
  if (kind === 'removed') return at(4 * g, IDEAL.rowZ.monster);
  if (kind === 'maindeck') return at(3 * g, IDEAL.rowZ.magic_trap);
  if (kind === 'extra') return at(-3 * g, IDEAL.rowZ.magic_trap);
  if (kind === 'hand') return at(0, IDEAL.rowZ.hand);
  // **並びの x は回さない**（番号は画面の左から。行そのものは相手の側＝ z だけ回す）
  return { x: (seq - 2) * g, z: (IDEAL.rowZ[kind] ?? IDEAL.rowZ.monster) * side };
}

/** 枠の縁に残す余白（平面のピクセル） */
export const FIT_MARGIN = 20;

/**
 * **理想のグリッドが全体の枠（平面）に収まるカードの幅**。
 *
 * 平面は実写と同じ（動画の幅 × 1260）で、カメラのプリセットはこの枠が舞台に収まるように解いてある。
 * 以前は `DEFAULT_UNIT`（110）のまま並べていて、横が 9 列ぶん（除外が外の列）で**枠から左右に約 200 はみ出していた**。
 * 枠を広げるとカメラの収まりが崩れるので、**グリッドのほうを枠に収める**（`unit` を縮める）。
 */
/**
 * @param {{w: number, h: number}} [plane]
 * @param {number} [max]
 */
export function fitUnit(plane = LIVE_PLANE, max = DEFAULT_UNIT) {
  let xMax = 0;
  let zMax = 0;
  ZONE_SLOTS.forEach(({ kind, player, seq }) => {
    const at = idealPosition(kind, player, seq);
    xMax = Math.max(xMax, Math.abs(at.x) + IDEAL.cardW / 2);
    zMax = Math.max(zMax, Math.abs(at.z) + IDEAL.cardH / 2);
  });
  const byW = (plane.w / 2 - FIT_MARGIN) / xMax;
  const byH = (plane.h / 2 - FIT_MARGIN) / zMax;
  return Math.max(1, Math.min(max, byW, byH));
}

/**
 * 仮想の Field。
 * @param {object} opts 省略可
 * @param {{w: number, h: number}} [opts.plane]
 * @param {number|null} [opts.unit] カード 1 枚の幅（平面のピクセル）。省くと**枠に収まる幅**（`fitUnit`）
 */
export function virtualField({ plane = LIVE_PLANE, unit = null } = {}) {
  const u = unit != null && unit > 0 ? unit : fitUnit(plane);
  const hw = (IDEAL.cardW / 2) * u;
  const hh = (IDEAL.cardH / 2) * u;
  const quads = {};
  ZONE_SLOTS.forEach(({ kind, player, seq }) => {
    const key = zoneKey(kind, player, seq);
    if (!key || quads[key]) return;
    const at = idealPosition(kind, player, seq);
    const cx = plane.w / 2 + at.x * u;
    const cy = plane.h / 2 + at.z * u;
    quads[key] = [[cx - hw, cy - hh], [cx + hw, cy - hh], [cx + hw, cy + hh], [cx - hw, cy + hh]];
  });
  return toField({ quads, plane, unit: u, virtual: true });
}
